import type { AxiosError } from "axios";
import { Sentry } from "@/sentry.ts";

const SENSITIVE_KEYS = [
	"password",
	"token",
	"secret",
	"authorization",
	"credit_card",
	"cvv",
];

/**
 * Redacts values whose key names look sensitive before shipping the object
 * to Sentry. Case-insensitive substring match — "authToken", "user_password",
 * etc. all get redacted.
 */
function sanitizeData(data: unknown): unknown {
	if (!data || typeof data !== "object") return data;
	const sanitized = { ...(data as Record<string, unknown>) };
	for (const key of Object.keys(sanitized)) {
		if (SENSITIVE_KEYS.some((sk) => key.toLowerCase().includes(sk))) {
			sanitized[key] = "[REDACTED]";
		}
	}
	return sanitized;
}

function shouldCaptureAsException(status: number): boolean {
	if (status >= 500) return true;
	if ([400, 403, 404, 429].includes(status)) return true;
	return false;
}

/**
 * Records API error signals to Sentry, redacting sensitive fields from the
 * captured request body. Extracted from main.tsx so the sanitization policy
 * lives alongside its unit-testable helpers instead of nested inside a render.
 */
export function reportApiRequestError(error: AxiosError): void {
	const originalRequest = error.config;

	const shouldAddBreadcrumb =
		error.response?.status !== 401 || originalRequest?._retry;

	if (!shouldAddBreadcrumb || !error.response) return;

	Sentry.addBreadcrumb({
		category: "api",
		message: `API Error: ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`,
		level: "error",
		data: {
			status: error.response.status,
			statusText: error.response.statusText,
		},
	});

	if (shouldCaptureAsException(error.response.status)) {
		Sentry.captureException(error, {
			tags: {
				type: "api_error",
				status: String(error.response.status),
				endpoint: originalRequest?.url,
				method: originalRequest?.method?.toUpperCase(),
			},
			extra: {
				responseData: error.response.data,
				requestData: sanitizeData(originalRequest?.data),
			},
		});
	}
}
